import { useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Controller, useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import {
  DateField,
  DateTimeField,
  FormError,
  FormFieldset,
  MultiSelectField,
  SimpleHTMLField,
  TextField,
  TextAreaField,
  ToggleField,
} from '@Forms/fields.jsx';
import {
  removeNewlines,
  slugifyDash,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

const BlogCategoryForm = ({
  blogCategoryData,
  disabled,
  mutation,
  onCancel,
  siteID,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    control,
    reset,
    resetField,
    setFocus,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: blogCategoryData,
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  // handling for tinymce fields
  const editorRefs = useRef({});

  useEffect(() => {
    // set focus to the name field if this is a new author
    if (!blogCategoryData.id) {
      setFocus('name');
    }
  }, [setFocus]);

  const onSubmit = (data) => {
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          label="Category name"
          name="name"
        />
        <TextAreaField
          register={register}
          error={errors.description}
          options={{ onChange: removeNewlines }}
          name="description"
          label="Description"
          helpText="Category description used as part of the meta description within category-level blog pages."
        />
        <ToggleField
          register={register}
          control={control}
          error={errors.is_post_type}
          name="is_post_type"
          label="Is post type?"
          helpText="This a special category type only used for filtering and should not be displayed"
        />
      </FormFieldset>
      {blogCategoryData.id && (
        <FormFieldset legend="Advanced">
          <TextField
            register={register}
            options={{ onChange: slugifyDash, onBlur: slugifyOnBlur }}
            error={errors.slug}
            name={`slug`}
            label="Category slug"
            helpText="Used for category filtering"
          />
        </FormFieldset>
      )}
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {onCancel && (
            <button
              className="sb-btn-cancel sb-btn"
              onClick={onCancel}
              disabled={disabled}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error publishing changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully published" />
        )}
      </div>
    </form>
  );
};

export default BlogCategoryForm;
